// Hydrate an existing profile document into the new wizard form shape, and
// build an empty form for new profiles. Preserves legacy fields so older
// profiles keep working without forced migration.

import { PHOTO_MIN } from "./constants";

export function emptyForm(role) {
  return {
    // Step 1 — Basic
    display_name: "",
    age: "",
    nationality: "",
    country_code: "ME",
    city: "",
    area: "",
    languages: [],
    availability_status: "available_today",
    is_independent: role !== "agency",
    headline: "",
    // Step 2 — Appearance
    height: "",
    weight: "",
    hair_color: "",
    eye_color: "",
    body_type: "",
    bust_size: "",
    dress_size: "",
    shoe_size: "",
    tattoos: false,
    piercings: false,
    smoking: "",
    drinking: "",
    fitness_level: "",
    // Step 3 — Gallery
    gallery: [],          // [{url, type:'image'|'video', is_main, position}]
    cover_photo: "",
    // Step 4 — About
    bio: "",
    personality: [],
    interests: [],
    hobbies: [],
    education: "",
    travel_available: false,
    favourite_activities: [],
    // Step 5 — Services
    services_matrix: {},  // {service_key: {status, price?}}
    // Step 6 — Pricing
    currency: "EUR",
    // NEW: primary In-call / Out-call split. At least one required.
    incall_price: "",
    outcall_price: "",
    pricing: { "1h":"", "2h":"", "3h":"", "6h":"", "12h":"", overnight:"", weekend:"", custom: [] },
    pricing_modes: [],    // ['incall','outcall','travel']
    // Step 7 — Schedule
    working_hours: {
      mon: { open: "10:00", close: "22:00", closed: false },
      tue: { open: "10:00", close: "22:00", closed: false },
      wed: { open: "10:00", close: "22:00", closed: false },
      thu: { open: "10:00", close: "22:00", closed: false },
      fri: { open: "10:00", close: "22:00", closed: false },
      sat: { open: "12:00", close: "00:00", closed: false },
      sun: { open: "12:00", close: "00:00", closed: true  },
    },
    available_now: false,
    vacation_mode: false,
    is_24_7: false,
    // Step 8 — Contact
    contact_prefs: { phone:false, whatsapp:true, telegram:false, viber:false, email:false, in_app:true },
    contact_details: {
      phone:    { country_code: "+382", number: "" },
      whatsapp: { country_code: "+382", number: "" },
      telegram: { username: "" },
      viber:    { country_code: "+382", number: "" },
      email:    { address: "" },
    },
    contact_languages: [],
    response_time: "under_1h",
    contact_visibility: "public",
  };
}

/**
 * Map a legacy/existing profile document onto the new wizard form, so users
 * with old profiles can edit them in the new wizard without losing data.
 */
export function hydrateForm(p, role) {
  if (!p) return emptyForm(role);
  const base = emptyForm(role);
  return {
    ...base,
    display_name: p.display_name || p.name || "",
    headline: p.headline || "",
    age: p.age ?? "",
    nationality: p.nationality || "",
    country_code: p.country_code || "ME",
    city: p.city || "",
    area: p.area || "",
    languages: p.languages || [],
    is_independent: p.is_independent ?? (role !== "agency"),
    availability_status: p.available_now ? "available_now"
      : p.available_today ? "available_today"
      : (p.vacation_mode ? "vacation" : "by_appointment"),

    height: p.height ?? "",
    weight: p.weight ?? "",
    hair_color: p.hair_color || "",
    eye_color: p.eye_color || "",
    body_type: p.body_type || "",
    bust_size: p.bust_size || "",
    dress_size: p.dress_size || "",
    shoe_size: p.shoe_size || "",
    tattoos: !!p.tattoos,
    piercings: !!p.piercings,
    smoking: p.smoking || "",
    drinking: p.drinking || "",
    fitness_level: p.fitness_level || "",

    gallery: Array.isArray(p.gallery) && p.gallery.length
      ? p.gallery
      : buildGalleryFromLegacy(p),
    cover_photo: p.cover_photo || "",

    bio: p.bio || p.description || "",
    personality: Array.isArray(p.personality) ? p.personality
      : (p.personality ? [p.personality] : []),
    interests: p.interests || [],
    hobbies: p.hobbies || [],
    education: p.education || "",
    travel_available: !!p.travel_available,
    favourite_activities: p.favourite_activities || [],

    services_matrix: p.services_matrix || buildMatrixFromLegacy(p),

    currency: p.currency || "EUR",
    incall_price: p.incall_price ?? "",
    outcall_price: p.outcall_price ?? "",
    pricing: { ...base.pricing, ...(p.pricing || mapLegacyRates(p.rates)) },
    pricing_modes: p.pricing_modes || p.availability_types || [],

    working_hours: { ...base.working_hours, ...(p.working_hours || {}) },
    available_now: !!p.available_now,
    vacation_mode: !!p.vacation_mode,
    is_24_7: !!p.is_24_7,

    contact_prefs: { ...base.contact_prefs, ...(p.contact_prefs || {}) },
    contact_details: {
      ...base.contact_details,
      ...(p.contact_details || {}),
      phone:    { ...base.contact_details.phone,    ...(p.contact_details?.phone    || {}) },
      whatsapp: { ...base.contact_details.whatsapp, ...(p.contact_details?.whatsapp || {}) },
      telegram: { ...base.contact_details.telegram, ...(p.contact_details?.telegram || {}) },
      viber:    { ...base.contact_details.viber,    ...(p.contact_details?.viber    || {}) },
      email:    { ...base.contact_details.email,    ...(p.contact_details?.email    || {}) },
    },
    contact_languages: p.contact_languages || p.languages || [],
    response_time: p.response_time || "under_1h",
    contact_visibility: p.contact_visibility || "public",
  };
}

function buildGalleryFromLegacy(p) {
  const out = [];
  const photos = Array.isArray(p.photos) ? p.photos : [];
  const main = p.main_photo;
  photos.forEach((url, i) => {
    out.push({ url, type: "image", is_main: url === main, position: i });
  });
  if (main && !photos.includes(main)) {
    out.unshift({ url: main, type: "image", is_main: true, position: -1 });
  }
  (Array.isArray(p.videos) ? p.videos : []).forEach((url, i) => {
    out.push({ url, type: "video", is_main: false, position: photos.length + i });
  });
  return out;
}

function buildMatrixFromLegacy(p) {
  const matrix = {};
  (p.service_items || []).forEach((it) => {
    if (!it || !it.service_id) return;
    matrix[it.service_id] = {
      status: it.mode === "extra" ? "extra"
        : it.mode === "included" ? "included" : "available",
      price: it.price || "",
    };
  });
  return matrix;
}

function mapLegacyRates(rates) {
  if (!rates || typeof rates !== "object") return {};
  return { "1h": rates["1h"] || "", "2h": rates["2h"] || "", "3h": rates["3h"] || "",
           "6h": rates["6h"] || "", "12h": rates["12h"] || "",
           overnight: rates.overnight || "", weekend: rates.weekend || "" };
}

/**
 * Build the PATCH/POST payload from the wizard form. Includes legacy keys
 * so old read paths keep rendering correctly.
 */
export function toApiPayload(form, opts = {}) {
  const photos = form.gallery.filter(g => g.type === "image").map(g => g.url);
  const videos = form.gallery.filter(g => g.type === "video").map(g => g.url);
  const main = form.gallery.find(g => g.is_main && g.type === "image")?.url || photos[0] || "";
  // Map availability_status back into legacy booleans
  const avail = form.availability_status;
  // Normalise the split pricing values into numbers (or null when unset).
  const incall = form.incall_price !== "" && form.incall_price !== null && form.incall_price !== undefined
    ? Number(form.incall_price) : null;
  const outcall = form.outcall_price !== "" && form.outcall_price !== null && form.outcall_price !== undefined
    ? Number(form.outcall_price) : null;
  const priceFrom = [incall, outcall].filter(v => typeof v === "number" && !Number.isNaN(v)).sort((a, b) => a - b)[0] ?? null;
  return {
    // Legacy / required
    name: form.display_name || form.headline || "Untitled",
    city: form.city,
    area: form.area,
    nationality: form.nationality,
    description: form.bio,
    age: parseInt(form.age) || undefined,
    height: parseInt(form.height) || undefined,
    weight: parseInt(form.weight) || undefined,
    body_type: form.body_type || undefined,
    hair_color: form.hair_color || undefined,
    eye_color: form.eye_color || undefined,
    bust_size: form.bust_size || undefined,
    tattoos: form.tattoos,
    piercings: form.piercings,
    smoking: form.smoking || undefined,
    drinking: form.drinking || undefined,
    languages: form.languages,
    services: Object.entries(form.services_matrix)
      .filter(([, v]) => v && v.status && v.status !== "not_available")
      .map(([k]) => k),
    availability_types: form.pricing_modes,
    available_now: avail === "available_now",
    available_today: avail === "available_today" || avail === "available_now",
    rates: Object.fromEntries(Object.entries(form.pricing).filter(([k, v]) =>
      k !== "custom" && v !== "" && v !== null && v !== undefined).map(([k, v]) => [k, Number(v) || 0])),
    is_independent: form.is_independent,
    photos, videos, main_photo: main,
    // NEW: split pricing surfaces + derived legacy price_from for filters/sort.
    incall_price: incall,
    outcall_price: outcall,
    price_from: priceFrom,
    // New premium fields
    display_name: form.display_name,
    headline: form.headline,
    cover_photo: form.cover_photo || main,
    gallery: form.gallery,
    bio: form.bio,
    personality: form.personality,
    interests: form.interests,
    hobbies: form.hobbies,
    education: form.education,
    travel_available: form.travel_available,
    favourite_activities: form.favourite_activities,
    dress_size: form.dress_size || undefined,
    shoe_size: form.shoe_size || undefined,
    fitness_level: form.fitness_level || undefined,
    services_matrix: form.services_matrix,
    pricing: form.pricing,
    working_hours: form.working_hours,
    vacation_mode: form.vacation_mode,
    is_24_7: form.is_24_7,
    contact_prefs: form.contact_prefs,
    contact_details: form.contact_details,
    contact_languages: form.contact_languages,
    response_time: form.response_time,
    contact_visibility: form.contact_visibility,
    currency: form.currency,
    ...opts,
  };
}

/**
 * Per-step validators. Each returns null when valid or an error string.
 */
export function validateStep(stepKey, form) {
  switch (stepKey) {
    case "basic":
      if (!form.display_name?.trim()) return "Please enter a display name.";
      if (!form.age || +form.age < 18) return "Age must be 18 or above.";
      if (!form.city) return "Please choose a city.";
      if (!form.languages?.length) return "Pick at least one language.";
      return null;
    case "appearance":
      return null; // optional
    case "gallery":
      {
        const imgs = form.gallery.filter(g => g.type === "image").length;
        if (imgs < PHOTO_MIN) return `Upload at least ${PHOTO_MIN} photos.`;
        if (!form.gallery.some(g => g.is_main && g.type === "image"))
          return "Select a main profile photo.";
        return null;
      }
    case "about":
      if (!form.bio || form.bio.trim().length < 40)
        return "Bio must be at least 40 characters.";
      return null;
    case "services":
      {
        const any = Object.values(form.services_matrix || {})
          .some(v => v?.status && v.status !== "not_available");
        if (!any) return "Mark at least one service as available.";
        return null;
      }
    case "pricing":
      {
        const incall = form.incall_price;
        const outcall = form.outcall_price;
        const hasIncall = incall !== "" && incall !== null && incall !== undefined && Number(incall) > 0;
        const hasOutcall = outcall !== "" && outcall !== null && outcall !== undefined && Number(outcall) > 0;
        if (!hasIncall && !hasOutcall)
          return "Please enter either an In-call or Out-call price (at least one is required).";
        if (!form.pricing_modes.length)
          return "Choose at least one location mode (incall / outcall / travel).";
        return null;
      }
    case "schedule":
      return null;
    case "contact":
      {
        const prefs = form.contact_prefs || {};
        const det = form.contact_details || {};
        if (!Object.values(prefs).some(Boolean))
          return "Enable at least one contact channel.";
        if (prefs.phone    && !det.phone?.number?.trim())    return "Enter your phone number.";
        if (prefs.whatsapp && !det.whatsapp?.number?.trim()) return "Enter your WhatsApp number.";
        if (prefs.telegram && !det.telegram?.username?.trim()) return "Enter your Telegram username.";
        if (prefs.viber    && !det.viber?.number?.trim())    return "Enter your Viber number.";
        if (prefs.email)    {
          const a = (det.email?.address || "").trim();
          if (!a) return "Enter your email address.";
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a)) return "Enter a valid email address.";
        }
        return null;
      }
    default:
      return null;
  }
}
